import {ReactElement, useContext, useEffect, useRef, useState} from "react";
import {Button} from "@zendeskgarden/react-buttons";
import {Message} from "@zendeskgarden/react-forms";
import {toast} from "react-toastify";
import {AuthContext} from "../../../../contexts/AuthContext";
import {
    PayrollBonusFrequency,
    PayrollBonusType,
    PayrollCompensationItemCalculationType,
    PayrollCompensationItemType,
    PayrollCompensationType,
    PayrollDefaultLevel,
    PayrollDefaultLevelContextInput,
    PayrollRaiseFrequency,
    PayrollRaiseType,
    useGetPayrollCompensationItemsQuery,
    ListAllPayrollDefaultsQuery,
    useBulkSavePayrollDefaultsMutation,
} from "../../../../__generated__/generated_types";
import styles from "./PayrollDefaults.module.scss";
import DefaultsEarnings from "./sections/DefaultsEarnings";
import DefaultsBenefits from "./sections/DefaultsBenefits";
import DefaultsTaxesAndInsurance from "./sections/DefaultsTaxesAndInsurance";
import {round} from "../../../../utils/math";

export interface LocalBaseDefault {
    _localId: string;
    id?: string;
    compensationType: PayrollCompensationType | undefined;
    annualCompensation: string;
    hourlyCompensation: string;
    expectedHoursPerWeek: string;
}

export interface LocalRaiseDefault {
    _localId: string;
    id?: string;
    frequency: PayrollRaiseFrequency | null;
    raiseType: PayrollRaiseType | null;
    amount: string;
    raiseMonth: string;
    raiseDay: string;
    onetimeRaiseDate: string | undefined;
}

export interface LocalBonusDefault {
    _localId: string;
    id?: string;
    frequency: PayrollBonusFrequency | null;
    bonusType: PayrollBonusType | null;
    amount: string;
    bonusMonth: string;
    bonusDay: string;
    onetimeBonusDate: string | undefined;
}

export interface LocalOvertimeDefault {
    _localId: string;
    id?: string;
    hourlyCompensation: string;
    expectedHoursPerWeek: string;
    effectiveStartDate: string | undefined;
    effectiveEndDate: string | undefined;
}

export interface LocalCompItemDefault {
    _localId: string;
    id?: string;
    compensationItemId: string;
    compensationItemType: PayrollCompensationItemType;
    calculationType: PayrollCompensationItemCalculationType;
    costPerPayPeriod: string;
    annualCostCap: string;
    effectiveStartDate: string | undefined;
}

export interface LoadedPayrollDefaults {
    bases: LocalBaseDefault[];
    raises: LocalRaiseDefault[];
    bonuses: LocalBonusDefault[];
    overtimes: LocalOvertimeDefault[];
    compItemDefaults: LocalCompItemDefault[];
}

interface ClientDefaultsFormProps {
    positionIds: string[];
    defaultLevel?: PayrollDefaultLevel;
    jurisdictionId?: string;
    jurisdictionIds?: string[];
    propertyId?: string;
    propertyIds?: string[];
    loadedDefaults?: LoadedPayrollDefaults;
    onSaved?: () => void;
    showResetControls?: boolean;
}

export default function DefaultsForm(props: ClientDefaultsFormProps): ReactElement {
    const {
        positionIds,
        defaultLevel = PayrollDefaultLevel.Client,
        jurisdictionId,
        jurisdictionIds,
        propertyId,
        propertyIds,
        loadedDefaults,
        onSaved,
        showResetControls = true,
    } = props;
    const {user} = useContext(AuthContext);
    const clientId = user?.clientId ?? "";

    const [bases, setBases] = useState<LocalBaseDefault[]>([]);
    const [raises, setRaises] = useState<LocalRaiseDefault[]>([]);
    const [bonuses, setBonuses] = useState<LocalBonusDefault[]>([]);
    const [overtimes, setOvertimes] = useState<LocalOvertimeDefault[]>([]);
    const [compItemDefaults, setCompItemDefaults] = useState<LocalCompItemDefault[]>([]);

    const loadedBasesState = useRef<LocalBaseDefault[]>([]);
    const loadedRaisesState = useRef<LocalRaiseDefault[]>([]);
    const loadedBonusesState = useRef<LocalBonusDefault[]>([]);
    const loadedOvertimesState = useRef<LocalOvertimeDefault[]>([]);
    const loadedCompItemsState = useRef<LocalCompItemDefault[]>([]);

    const [saving, setSaving] = useState(false);
    const [hasEarningsErrors, setHasEarningsErrors] = useState(false);
    const [hasBenefitsErrors, setHasBenefitsErrors] = useState(false);
    const [hasTaxesErrors, setHasTaxesErrors] = useState(false);

    const {data: compItemsData} = useGetPayrollCompensationItemsQuery({
        fetchPolicy: "no-cache", // Make sure we get the latest data matching current workspace user is viewing
    });
    const compensationItems = compItemsData?.listCompensationItems ?? [];

    const [bulkSave] = useBulkSavePayrollDefaultsMutation();
    const selectedJurisdictionIds = jurisdictionIds ?? (jurisdictionId ? [jurisdictionId] : []);
    const selectedPropertyIds = propertyIds ?? (propertyId ? [propertyId] : []);

    useEffect(() => {
        const nextDefaults = loadedDefaults ?? emptyLoadedDefaults();
        setBases(nextDefaults.bases);
        setRaises(nextDefaults.raises);
        setBonuses(nextDefaults.bonuses);
        setOvertimes(nextDefaults.overtimes);
        setCompItemDefaults(nextDefaults.compItemDefaults);
        loadedBasesState.current = nextDefaults.bases;
        loadedRaisesState.current = nextDefaults.raises;
        loadedBonusesState.current = nextDefaults.bonuses;
        loadedOvertimesState.current = nextDefaults.overtimes;
        loadedCompItemsState.current = nextDefaults.compItemDefaults;
    }, [loadedDefaults]);

    function handleSave() {
        if(defaultLevel === PayrollDefaultLevel.Jurisdiction && selectedJurisdictionIds.length === 0) {
            toast.error("Please select a jurisdiction");
            return;
        }

        if(defaultLevel === PayrollDefaultLevel.Property && selectedPropertyIds.length === 0) {
            toast.error("Please select a property");
            return;
        }

        if(positionIds.length === 0) {
            toast.error("Please select a position");
            return;
        }

        if(hasValidationErrors) {
            toast.error("Please complete the required fields");
            return;
        }

        setSaving(true);
        const defaultBase = bases[0];
        const base = defaultBase ? {
            compensationType: defaultBase.compensationType!,
            annualCompensation: defaultBase.annualCompensation || undefined,
            hourlyCompensation: defaultBase.hourlyCompensation || undefined,
            expectedHoursPerWeek: defaultBase.expectedHoursPerWeek || undefined,
        } : undefined;

        const overtimeInputs = overtimes
            .map(o => ({
                hourlyCompensation: o.hourlyCompensation,
                expectedHoursPerWeek: o.expectedHoursPerWeek,
                effectiveStartDate: o.effectiveStartDate!,
                effectiveEndDate: o.effectiveEndDate || undefined,
            }));

        const raiseInputs = raises
            .map(r => ({
                frequency: r.frequency!,
                raiseType: r.raiseType!,
                amount: r.amount,
                raiseMonth: r.raiseMonth ? parseInt(r.raiseMonth) : undefined,
                raiseDay: r.raiseDay ? parseInt(r.raiseDay) : undefined,
                fixedRaiseDate: r.onetimeRaiseDate || undefined,
            }));

        const bonusInputs = bonuses
            .map(b => ({
                frequency: b.frequency!,
                bonusType: b.bonusType!,
                amount: b.amount,
                bonusMonth: b.bonusMonth ? parseInt(b.bonusMonth) : undefined,
                bonusDay: b.bonusDay ? parseInt(b.bonusDay) : undefined,
                oneTimeBonusDate: b.onetimeBonusDate || undefined,
            }));

        const itemInputs = compItemDefaults
            .map(ci => ({
                compensationItemId: ci.compensationItemId,
                calculationType: ci.calculationType,
                costPerPayPeriod: ci.costPerPayPeriod,
                annualCostCap: ci.annualCostCap || undefined,
                effectiveStartDate: ci.effectiveStartDate!,
            }));

        const createContext: PayrollDefaultLevelContextInput[] = [];

        positionIds.forEach(positionId => {
            if(defaultLevel === PayrollDefaultLevel.Jurisdiction) {
                selectedJurisdictionIds.forEach(selectedJurisdictionId => createContext.push({
                    defaultLevel,
                    positionId,
                    clientId,
                    jurisdictionId: selectedJurisdictionId,
                    propertyId: undefined,
                }));
                return;
            }

            if(defaultLevel === PayrollDefaultLevel.Property) {
                selectedPropertyIds.forEach(selectedPropertyId => createContext.push({
                    defaultLevel,
                    positionId,
                    clientId,
                    jurisdictionId: undefined,
                    propertyId: selectedPropertyId,
                }));
                return;
            }

            createContext.push({
                defaultLevel,
                positionId,
                clientId,
                jurisdictionId: undefined,
                propertyId: undefined,
            });
        });

        bulkSave({
            variables: {
                createContext,
                base,
                overtimes: overtimeInputs,
                raises: raiseInputs,
                bonuses: bonusInputs,
                items: itemInputs,
            }
        }).then(result => {
            if(result.data) {
                onSaved?.();
                toast.success("Defaults saved successfully");
                return;
            }
            toast.error("Failed to save defaults");
        }).catch(() => {
            toast.error("Failed to save defaults");
        }).finally(() => {
            setSaving(false);
        });
    }

    // Dirty checking
    function arraysEqual<T extends {_localId: string}>(a: T[], b: T[]): boolean {
        if(a.length !== b.length) {
            return false;
        }
        const aSorted = a.sortBy(a => a._localId);
        const bSorted = b.sortBy(b => b._localId);
        return JSON.stringify(aSorted) === JSON.stringify(bSorted);
    }

    const basesChanged = showResetControls && !arraysEqual(bases, loadedBasesState.current);
    const raisesChanged = showResetControls && !arraysEqual(raises, loadedRaisesState.current);
    const bonusesChanged = showResetControls && !arraysEqual(bonuses, loadedBonusesState.current);
    const overtimesChanged = showResetControls && !arraysEqual(overtimes, loadedOvertimesState.current);

    function compItemsChangedForType(type: PayrollCompensationItemType): boolean {
        const current = compItemDefaults.filter(ci => ci.compensationItemType === type);
        const loaded = loadedCompItemsState.current.filter(ci => ci.compensationItemType === type);
        return showResetControls && !arraysEqual(current, loaded);
    }

    const hasAnyChanges = basesChanged || raisesChanged || bonusesChanged || overtimesChanged
        || !arraysEqual(compItemDefaults, loadedCompItemsState.current);
    const hasValidationErrors = hasEarningsErrors || hasBenefitsErrors || hasTaxesErrors;
    const disableSave = saving || hasValidationErrors || (showResetControls && !hasAnyChanges);
    const isFormEmpty = bases.length === 0
        && raises.length === 0
        && bonuses.length === 0
        && overtimes.length === 0
        && compItemDefaults.length === 0;

    // Filter comp items for benefits vs taxes sections
    const benefitTypes = [
        PayrollCompensationItemType.Medical,
        PayrollCompensationItemType.Dental,
        PayrollCompensationItemType.Vision,
        PayrollCompensationItemType.Retirement_401KMatch,
    ];
    const benefitCompItems = compItemDefaults.filter(ci => benefitTypes.includes(ci.compensationItemType));
    const taxCompItems = compItemDefaults.filter(ci => !benefitTypes.includes(ci.compensationItemType));

    function resetAll() {
        setBases(loadedBasesState.current);
        setRaises(loadedRaisesState.current);
        setBonuses(loadedBonusesState.current);
        setOvertimes(loadedOvertimesState.current);
        setCompItemDefaults(loadedCompItemsState.current);
    }

    function resetCompItemType(type: PayrollCompensationItemType) {
        const others = compItemDefaults.filter(ci => ci.compensationItemType !== type);
        const loadedForType = loadedCompItemsState.current.filter(ci => ci.compensationItemType === type);
        setCompItemDefaults([...others, ...loadedForType]);
    }

    function setBenefitCompItems(items: LocalCompItemDefault[]) {
        setCompItemDefaults([...items, ...taxCompItems]);
    }

    function setTaxCompItems(items: LocalCompItemDefault[]) {
        setCompItemDefaults([...benefitCompItems, ...items]);
    }

    return (
        <div className={styles.formContainer}>
            <DefaultsEarnings
                bases={bases}
                raises={raises}
                bonuses={bonuses}
                overtimes={overtimes}
                setBases={setBases}
                setRaises={setRaises}
                setBonuses={setBonuses}
                setOvertimes={setOvertimes}
                onResetBases={() => setBases(loadedBasesState.current)}
                onResetRaises={() => setRaises(loadedRaisesState.current)}
                onResetBonuses={() => setBonuses(loadedBonusesState.current)}
                onResetOvertimes={() => setOvertimes(loadedOvertimesState.current)}
                basesChanged={basesChanged}
                raisesChanged={raisesChanged}
                bonusesChanged={bonusesChanged}
                overtimesChanged={overtimesChanged}
                onValidationChange={setHasEarningsErrors}
                showResetControls={showResetControls}
            />
            <DefaultsBenefits
                compItemDefaults={benefitCompItems}
                setCompItemDefaults={setBenefitCompItems}
                compensationItems={compensationItems}
                onResetType={resetCompItemType}
                isTypeChanged={compItemsChangedForType}
                onValidationChange={setHasBenefitsErrors}
                showResetControls={showResetControls}
            />
            <DefaultsTaxesAndInsurance
                compItemDefaults={taxCompItems}
                setCompItemDefaults={setTaxCompItems}
                compensationItems={compensationItems}
                onResetType={resetCompItemType}
                isTypeChanged={compItemsChangedForType}
                onValidationChange={setHasTaxesErrors}
                showResetControls={showResetControls}
            />
            <div className={styles.saveBar}>
                {showResetControls && <Button isBasic onClick={resetAll} disabled={saving || !hasAnyChanges}>Reset</Button>}
                <Button isPrimary className={styles.saveButton} disabled={disableSave} onClick={handleSave}>
                    {saving ? "Saving..." : "Save"}
                </Button>
            </div>
            {isFormEmpty && !disableSave && (
                <div className={styles.saveWarning}>
                    <Message validation="warning">
                        Saving an empty form will delete all defaults.
                    </Message>
                </div>
            )}
        </div>
    );
}

function moneyToStringRounded(val: any): string {
    if(val === null || val === undefined) {
        return "";
    }
    return round(val, 2).toFixed(2).toString();
}

export function mapPayrollDefaults(data: ListAllPayrollDefaultsQuery): LoadedPayrollDefaults {
    return {
        bases: (data.listPayrollDefaultBases ?? []).map(b => ({
            _localId: b.id,
            id: b.id,
            compensationType: b.compensationType,
            annualCompensation: moneyToStringRounded(b.annualCompensation),
            hourlyCompensation: moneyToStringRounded(b.hourlyCompensation),
            expectedHoursPerWeek: b.expectedHoursPerWeek?.toString() ?? "",
        })),
        overtimes: (data.listPayrollDefaultOvertimes ?? []).map(o => ({
            _localId: o.id,
            id: o.id,
            hourlyCompensation: moneyToStringRounded(o.hourlyCompensation),
            expectedHoursPerWeek: o.expectedHoursPerWeek.toString(),
            effectiveStartDate: o.effectiveStartDate,
            effectiveEndDate: o.effectiveEndDate ?? undefined,
        })),
        raises: (data.listPayrollDefaultRaises ?? []).map(r => ({
            _localId: r.id,
            id: r.id,
            frequency: r.frequency,
            raiseType: r.raiseType,
            amount: moneyToStringRounded(r.amount),
            raiseMonth: r.raiseMonth?.toString() ?? "",
            raiseDay: r.raiseDay?.toString() ?? "",
            onetimeRaiseDate: r.onetimeRaiseDate ?? undefined,
        })),
        bonuses: (data.listPayrollDefaultBonuses ?? []).map(b => ({
            _localId: b.id,
            id: b.id,
            frequency: b.frequency,
            bonusType: b.bonusType,
            amount: moneyToStringRounded(b.amount),
            bonusMonth: b.bonusMonth?.toString() ?? "",
            bonusDay: b.bonusDay?.toString() ?? "",
            onetimeBonusDate: b.onetimeBonusDate ?? undefined,
        })),
        compItemDefaults: (data.listPayrollDefaultCompensationItems ?? []).map(ci => ({
            _localId: ci.id,
            id: ci.id,
            compensationItemId: ci.compensationItem.id,
            compensationItemType: ci.compensationItem.type,
            calculationType: ci.calculationType,
            costPerPayPeriod: moneyToStringRounded(ci.costPerPayPeriod),
            annualCostCap: moneyToStringRounded(ci.annualCostCap),
            effectiveStartDate: ci.effectiveStartDate,
        })),
    };
}

export function emptyLoadedDefaults(): LoadedPayrollDefaults {
    return {
        bases: [],
        raises: [],
        bonuses: [],
        overtimes: [],
        compItemDefaults: [],
    };
}
